import type { ApiError } from '@api/api';

export const API_BASE = '/api';

/** Error thrown for any non-2xx answer (or a network failure: status 0). */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

export function isApiError(e: unknown, code?: string): e is ApiRequestError {
  return e instanceof ApiRequestError && (code === undefined || e.code === code);
}

/** Owner-friendly wording for error codes whose server message is technical. */
export const FRIENDLY: Record<string, string> = {
  network: 'Cannot reach the panel server. Check the connection.',
  csrf: 'The request was blocked as cross-site. Reload the page.',
  rate_limited: 'Too many attempts. Wait a few minutes and try again.',
  invalid_credentials: 'Wrong e-mail or password.',
  invalid_code: 'Wrong code. Wait for the next one and try again.',
  forbidden: 'Your role (viewer) cannot do this.',
  clone_busy: 'Another job is running for this clone. Wait for it to finish.',
  invalid_state: 'Not possible in the current state.',
  not_implemented: 'This action is not available on the server yet.',
  last_owner: 'There must be at least one active owner.',
  release_missing: 'Choose a release for the clone first (Settings tab).',
  release_not_ready: 'The release is still being fetched or failed.',
  self: 'You cannot change your own account here.',
  token_not_allowed: 'API tokens cannot be used for this.',
  totp_cancelled: 'Two-factor confirmation cancelled.',
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiRequestError) {
    const f = FRIENDLY[e.code];
    if (!f) return e.message || e.code;
    return e.message && e.message !== f && !['network', 'csrf', 'forbidden', 'totp_cancelled', 'invalid_credentials'].includes(e.code) ? `${f} (${e.message})` : f;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

export interface AuthHandlers {
  /** 401: the session is gone → show the login screen. */
  onUnauthenticated: () => void;
  /** 403 mfa_required: logged in but TOTP not passed → show the TOTP screen. */
  onMfaRequired: () => void;
  /** 403 totp_required: ask the owner for a fresh code; null = cancelled. */
  promptTotp: (reason: string) => Promise<string | null>;
}

let handlers: AuthHandlers = {
  onUnauthenticated: () => {},
  onMfaRequired: () => {},
  promptTotp: async () => null,
};

export function setAuthHandlers(h: Partial<AuthHandlers>): void {
  handlers = { ...handlers, ...h };
}

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  signal?: AbortSignal;
  /** Don't route 401 / mfa_required to the global handlers (login and TOTP screens). */
  noAuthRedirect?: boolean;
  /** How many fresh TOTP codes to ask for before giving up. */
  totpAttempts?: number;
}

const MAX_TOTP_ATTEMPTS = 3;

function withTotp(body: unknown, code: string): unknown {
  if (body instanceof FormData) {
    const fd = new FormData();
    body.forEach((v, k) => {
      if (k !== 'totp') fd.append(k, v);
    });
    fd.append('totp', code);
    return fd;
  }
  const base = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  return { ...base, totp: code };
}

async function parseError(res: Response): Promise<ApiRequestError> {
  let code = `http_${res.status}`;
  let message = res.statusText || `Request failed (${res.status})`;
  try {
    const data = (await res.json()) as Partial<ApiError>;
    if (data && data.error) {
      code = data.error.code || code;
      message = data.error.message || message;
    }
  } catch {
    /* not JSON */
  }
  return new ApiRequestError(res.status, code, message);
}

export async function request<T>(method: Method, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers['x-requested-with'] = 'control';
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(API_BASE + path, { method, headers, body: payload, credentials: 'include', signal: opts.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiRequestError(0, 'network', 'Cannot reach the panel server');
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  const err = await parseError(res);
  if (!opts.noAuthRedirect) {
    if (err.status === 401) handlers.onUnauthenticated();
    else if (err.status === 403 && err.code === 'mfa_required') handlers.onMfaRequired();
  }
  if (err.status === 403 && (err.code === 'totp_required' || err.code === 'totp_invalid') && method !== 'GET') {
    const left = opts.totpAttempts ?? MAX_TOTP_ATTEMPTS;
    if (left > 0) {
      const reason = err.code === 'totp_invalid' || (body as { totp?: string } | undefined)?.totp ? 'The code was not accepted. Try a new one.' : err.message;
      const code = await handlers.promptTotp(reason);
      if (!code) throw new ApiRequestError(403, 'totp_cancelled', 'Two-factor confirmation cancelled');
      return request<T>(method, path, withTotp(body, code), { ...opts, totpAttempts: left - 1 });
    }
  }
  throw err;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body ?? {}, opts),
  del: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, body, opts),
};

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
